import { useState, useMemo, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import DaumPostcode from "react-daum-postcode";
import Container from "@/components/layout/Container";
import PageTitleBox from "@/components/common/PageTitleBox";
import { useServiceSubmit, ServiceType, BusinessType } from "@/hooks/useServiceSubmit";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";
import { ArrowDownSquare } from "react-iconly";

// ============================================
// 서비스 유형 설정 (확장 가능한 구조)
// ============================================
interface FileField {
  id: string;
  label: string;
  required: boolean;
  helpText?: string;
}

interface ServiceConfig {
  id: ServiceType;
  name: string;
  pageTitle: string;
  pageSubtitle: string;
  showProgressSteps?: boolean;
  progressSteps?: { label: string; active: boolean }[];
  purposeOptions?: { value: string; label: string; hint?: string }[];
  companyTypeOptions?: { value: string; label: string; hint?: string }[];
  showAgentIp?: boolean;
  showWebhookUrl?: boolean;
  businessTypes?: {
    type: BusinessType;
    label: string;
    description: string;
    files: FileField[];
  }[];
  successModal: {
    title: string;
    message: React.ReactNode;
    showTestButton?: boolean;
    testButtonLabel?: string;
  };
}

// 서비스별 설정 정의
const SERVICE_CONFIGS: Record<ServiceType, ServiceConfig> = {
  messaging: {
    id: "messaging",
    name: "메시징 서비스",
    pageTitle: "서비스 이용 신청하기",
    pageSubtitle: "문의하실 내용을 작성해 주세요.",
    showProgressSteps: false,
    progressSteps: [
      { label: "서비스 이용 신청", active: true },
      { label: "서비스 정식 계약", active: false },
    ],
    purposeOptions: [
      { value: "info", label: "정보성 알림", hint: "예: 예약 확정, 출고 안내, 인증번호" },
      { value: "marketing", label: "영업 및 마케팅 광고", hint: "예: 이벤트 홍보, 할인 쿠폰" },
      { value: "notice", label: "기관 안내 및 공지", hint: "예: 학사 공지, 기관 소식지" },
      { value: "other", label: "기타", hint: "직접입력" },
    ],
    companyTypeOptions: [
      { value: "reseller", label: "문자 재판매사" },
      { value: "hosting", label: "문자 호스팅" },
      { value: "outsourcing", label: "문자 발송대행사 운영" },
      { value: "agency", label: "대행 외" },
      { value: "solution", label: "솔루션" },
      { value: "idc", label: "IDC 서비스" },
      { value: "webagency", label: "웹 에이전시" },
      { value: "other", label: "기타", hint: "직접입력" },
    ],
    showAgentIp: true,
    showWebhookUrl: true,
    businessTypes: [
      {
        type: "general",
        label: "일반",
        description: "일반 업체의 경우 재직증명서, 통신사이용증명원 등의 서류가 필요합니다.",
        files: [
          {
            id: "businessLicense",
            label: "사업자등록증 사본",
            required: true,
            helpText: "",
          },
          {
            id: "corporateSealCert",
            label: "법인 인감 증명서",
            required: true,
            helpText: "",
          },
          {
            id: "callerIdDoc",
            label: "발신번호 등록서류",
            required: true,
            helpText: "",
          },
          {
            id: "telecomCert",
            label: "통신사이용증명원",
            required: true,
            helpText: "",
          },
          {
            id: "employmentCert",
            label: "재직증명서",
            required: true,
            helpText: "",
          },

          {
            id: "callerIdConsent",
            label: "발신번호 변작방지 및 사전동의서",
            required: true,
            helpText: "우측 상단 문서를 다운받아 작성해주세요.",
          },
        ],
      },
      {
        type: "special",
        label: "특수부가통신사업자",
        description:
          "특수부가통신사업자의 경우 특수부가통신사업자 등록증, 전송자격인증서 등의 서류가 필요합니다.",
        files: [
          {
            id: "businessLicense",
            label: "사업자등록증 사본",
            required: true,
            helpText: "",
          },
          {
            id: "corporateSealCert",
            label: "법인 인감 증명서",
            required: true,
            helpText: "",
          },
          {
            id: "callerIdDoc",
            label: "발신번호 등록서류",
            required: true,
            helpText: "",
          },
          {
            id: "specialTelecomCert",
            label: "특수부가통신사업자 등록증",
            required: true,
            helpText: "",
          },
          {
            id: "transmissionCert",
            label: "전송자격인증서",
            required: true,
            helpText: "",
          },
        ],
      },
    ],
    successModal: {
      title: "메시징 서비스 신청 완료",
      message: (
        <>
          담당자 검토 후, 기재하신 이메일로
          <br />
          <strong>테스트 계정과 이용 가이드</strong>를 보내드리겠습니다.
          <br />
          <span className="text-gray-5 text-[1.3rem]">
            (검토는 영업일 기준 최대 1~2일이 소요될 수 있습니다.)
          </span>
          <br />
          <br />
          <span className="text-[#4361ee]">테스트 후 정식 계약을 원하시는 경우,</span>
          <br />
          <span className="text-[#4361ee]">이메일을 통한 서류 제출 절차가 필요할 수 있습니다.</span>
        </>
      ),
      showTestButton: true,
      testButtonLabel: "메시지 발송 테스트 진행하기",
    },
  },
  aicc: {
    id: "aicc",
    name: "문자콜센터 서비스",
    pageTitle: "정확한 서비스 제공과 안전한 계약 절차를 위해",
    pageSubtitle: "몇 가지 정보를 입력해 주세요",
    showProgressSteps: false,
    businessTypes: [
      {
        type: "general",
        label: "일반",
        description: "일반 업체의 경우 재직증명서, 통신사이용증명원 등의 서류가 필요합니다.",
        files: [
          {
            id: "businessLicense",
            label: "사업자등록증 사본",
            required: true,
            helpText: "",
          },
          {
            id: "employmentCert",
            label: "재직증명서",
            required: true,
            helpText: "",
          },
          {
            id: "corporateSealCert",
            label: "법인 인감 증명서",
            required: true,
            helpText: "",
          },
          {
            id: "callerIdDoc",
            label: "발신번호 등록서류",
            required: true,
            helpText: "",
          },
          {
            id: "telecomCert",
            label: "통신사이용증명원",
            required: true,
            helpText: "",
          },

          {
            id: "callerIdConsent",
            label: "발신번호 변작방지 및 사전동의서",
            required: true,
            helpText: "우측 상단 문서를 다운받아 작성해주세요.",
          },
        ],
      },
      {
        type: "reseller",
        label: "재판매사",
        description: "재판매사의 경우 별도의 서류가 필요합니다.",
        files: [
          {
            id: "businessLicense",
            label: "사업자등록증 사본",
            required: true,
            helpText: "",
          },
          {
            id: "corporateSealCert",
            label: "법인 인감 증명서",
            required: true,
            helpText: "",
          },
          {
            id: "callerIdDoc",
            label: "발신번호 등록서류",
            required: true,
            helpText: "",
          },
          {
            id: "specialTelecomCert",
            label: "특수부가통신사업자 등록증",
            required: true,
            helpText: "",
          },
          {
            id: "transmissionCert",
            label: "전송자격인증서",
            required: true,
            helpText: "",
          },
        ],
      },
    ],
    successModal: {
      title: "문자콜센터 서비스 신청 완료",
      message: (
        <>
          담당자 검토 후, 기재하신 연락처로
          <br />
          서비스에 대한 안내를 드릴 예정입니다.
          <br />
          감사합니다.
          <br />
          <span className="text-gray-5 text-[1.3rem]">
            (검토는 영업일 기준 최대 1~2일이 소요될 수 있습니다.)
          </span>
        </>
      ),
      showTestButton: false,
    },
  },
};

// 공통 스타일
const inputStyle: React.CSSProperties = {
  minHeight: "44px",
  padding: "0.75rem 1rem",
  border: "1px solid #e5e5e5",
  borderRadius: "9px",
  fontSize: "1.5rem",
  backgroundColor: "#fafafa",
};

const labelStyle: React.CSSProperties = {
  fontSize: "1.6rem",
  fontWeight: 500,
  color: "#1a1a1a",
};

// 컴포넌트
const ServiceSubmit = () => {
  const [searchParams] = useSearchParams();
  const serviceTypeParam = searchParams.get("type") as ServiceType | null;

  const [serviceType, setServiceType] = useState<ServiceType>(
    serviceTypeParam === "messaging" ? "messaging" : "messaging"
  );

  const config = SERVICE_CONFIGS[serviceType];

  const [businessType, setBusinessType] = useState<BusinessType>(
    config.businessTypes?.[0]?.type || "general"
  );

  // 백엔드 필드명과 일치하게 변경
  const [formData, setFormData] = useState({
    contactName: "",
    deptName: "",
    jobTitle: "",
    contactPhone: "",
    corporateName: "",
    industry: "",
    bizRegNo: "",
    contactEmail: "",
    zipCode: "",
    address: "",
    addressDetail: "",
    purposeOther: "",
    companyTypeOther: "",
    agentServerIp: "",
    webhookUrl: "",
    sdkDomain: "",
  });

  // 중복 선택 가능한 필드
  const [purposes, setPurposes] = useState<string[]>([]);
  const [companyTypes, setCompanyTypes] = useState<string[]>([]);

  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<"alert" | "success">("alert");
  const [modalMessage, setModalMessage] = useState("");
  const [requestId, setRequestId] = useState<number | null>(null);

  // Daum Postcode 모달 상태
  const [isPostcodeOpen, setIsPostcodeOpen] = useState(false);

  // 파일 상태
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const navigate = useNavigate();

  // TanStack Query mutation
  const submitMutation = useServiceSubmit();

  const currentBusinessConfig = useMemo(() => {
    return config.businessTypes?.find(bt => bt.type === businessType);
  }, [config, businessType]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === "contactPhone" || name === "bizRegNo") {
      const numericValue = value.replace(/[^0-9]/g, "");
      setFormData(prev => ({ ...prev, [name]: numericValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (fieldId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // if (file.size > 20 * 1024 * 1024) {
      //   showAlert("파일 크기는 20MB 이내여야 합니다.");
      //   e.target.value = "";
      //   return;
      // }
      setFiles(prev => ({ ...prev, [fieldId]: file }));
    }
  };

  // Daum Postcode 완료 핸들러
  const handlePostcodeComplete = (data: any) => {
    setFormData(prev => ({
      ...prev,
      zipCode: data.zonecode,
      address: data.address,
    }));
    setIsPostcodeOpen(false);
  };

  const showAlert = (message: string) => {
    setModalMessage(message);
    setModalType("alert");
    setShowModal(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (submitMutation.isPending) return;

    // 필수 파일 체크
    const requiredFiles = currentBusinessConfig?.files.filter(f => f.required) || [];
    for (const field of requiredFiles) {
      if (!files[field.id]) {
        showAlert(`${field.label}을(를) 첨부해주세요.`);
        return;
      }
    }

    // 필수 입력값 체크
    if (
      !formData.contactName ||
      !formData.contactPhone ||
      !formData.corporateName ||
      !formData.contactEmail
    ) {
      showAlert("필수 정보를 모두 입력해주세요.");
      return;
    }

    // 이메일 형식 체크
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.contactEmail)) {
      showAlert("이메일 주소를 올바른 형식으로 입력해주세요.");
      return;
    }

    // serviceKind 생성
    let serviceKind = "";
    if (serviceType === "messaging") {
      if (businessType === "general") {
        serviceKind = "메시징-일반";
      } else if (businessType === "special") {
        serviceKind = "메시징-특수";
      }
    } else if (serviceType === "aicc") {
      if (businessType === "general") {
        serviceKind = "aicc-일반";
      } else if (businessType === "reseller") {
        serviceKind = "aicc-재판매사";
      }
    }

    // mutation 실행
    submitMutation.mutate(
      {
        serviceType,
        businessType,
        formData,
        purposes,
        companyTypes,
        files,
        serviceKind,
      },
      {
        onSuccess: data => {
          setRequestId(data.requestId);
          setModalType("success");
          setShowModal(true);
        },
        onError: error => {
          console.error("Service submit error:", error);
          showAlert("서비스 신청 중 오류가 발생했습니다.\n잠시 후 다시 시도해주세요.");
        },
      }
    );
  };

  const handleServiceTypeChange = (newType: ServiceType) => {
    setServiceType(newType);
    setBusinessType(SERVICE_CONFIGS[newType].businessTypes?.[0]?.type || "general");
    setFiles({});
  };

  return (
    <Container>
      <main className="py-[2rem] px-[2rem] about:px-0">
        <PageTitleBox
          title={config.pageTitle}
          subtitle={config.pageSubtitle}
          showSeparator={false}
          className="w-full max-w-[900px] mx-auto text-center mb-[40px]"
        />

        {config.showProgressSteps && config.progressSteps && (
          <div className="flex items-center justify-center gap-4 mb-[60px]">
            {config.progressSteps.map((step, idx) => (
              <div key={idx} className="flex items-center gap-4">
                <span
                  className={`text-[1.5rem] ${
                    step.active ? "text-[#4361ee] font-semibold" : "text-gray-4"
                  }`}
                >
                  {step.label}
                </span>
                {idx < config.progressSteps!.length - 1 && (
                  <span className="text-gray-3">—————</span>
                )}
              </div>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mx-auto max-w-[900px]">
          {/* 고객사 정보 */}
          <section className="mb-[60px]">
            <div className="flex items-center gap-5 mb-12">
              <h2 className="text-[2rem] font-bold text-navy">고객사 정보</h2>
              <div className="flex-1 h-[1px] bg-gray-400" />
            </div>

            <div className="flex flex-col sm:grid sm:grid-cols-2 gap-6 sm:gap-x-8 sm:gap-y-6 px-[30px]">
              <div className="flex flex-col gap-4">
                <label
                  htmlFor="contactName"
                  className="after:content-['*'] after:text-red-500 after:ml-1"
                  style={labelStyle}
                >
                  담당자
                </label>
                <input
                  type="text"
                  id="contactName"
                  name="contactName"
                  placeholder="이름을 입력해주세요."
                  value={formData.contactName}
                  onChange={handleChange}
                  required
                  className="w-full focus:outline-none focus:border-navy"
                  style={inputStyle}
                />
              </div>

              <div className="flex flex-col gap-4">
                <label
                  htmlFor="contactPhone"
                  className="after:content-['*'] after:text-red-500 after:ml-1"
                  style={labelStyle}
                >
                  연락처
                </label>
                <input
                  type="tel"
                  id="contactPhone"
                  name="contactPhone"
                  placeholder="'-'를 제외한 숫자를 입력해주세요."
                  value={formData.contactPhone}
                  onChange={handleChange}
                  required
                  className="w-full focus:outline-none focus:border-navy"
                  style={inputStyle}
                />
              </div>

              <div className="flex flex-col gap-4">
                <label
                  htmlFor="deptName"
                  className="after:content-['*'] after:text-red-500 after:ml-1"
                  style={labelStyle}
                >
                  부서명
                </label>
                <input
                  type="text"
                  id="deptName"
                  name="deptName"
                  placeholder="부서명을 입력해주세요."
                  value={formData.deptName}
                  onChange={handleChange}
                  required
                  className="w-full focus:outline-none focus:border-navy"
                  style={inputStyle}
                />
              </div>

              <div className="flex flex-col gap-4">
                <label
                  htmlFor="jobTitle"
                  className="after:content-['*'] after:text-red-500 after:ml-1"
                  style={labelStyle}
                >
                  직책
                </label>
                <input
                  type="text"
                  id="jobTitle"
                  name="jobTitle"
                  placeholder="직책을 입력해주세요."
                  value={formData.jobTitle}
                  onChange={handleChange}
                  required
                  className="w-full focus:outline-none focus:border-navy"
                  style={inputStyle}
                />
              </div>

              <div className="flex flex-col gap-4">
                <label
                  htmlFor="corporateName"
                  className="after:content-['*'] after:text-red-500 after:ml-1"
                  style={labelStyle}
                >
                  법인명
                </label>
                <input
                  type="text"
                  id="corporateName"
                  name="corporateName"
                  placeholder="법인명을 입력해주세요."
                  value={formData.corporateName}
                  onChange={handleChange}
                  required
                  className="w-full focus:outline-none focus:border-navy"
                  style={inputStyle}
                />
              </div>

              <div className="flex flex-col gap-4">
                <label
                  htmlFor="bizRegNo"
                  className="after:content-['*'] after:text-red-500 after:ml-1"
                  style={labelStyle}
                >
                  사업자등록번호
                </label>
                <input
                  type="text"
                  id="bizRegNo"
                  name="bizRegNo"
                  placeholder="'-'를 제외한 숫자 10자리를 입력해주세요."
                  value={formData.bizRegNo}
                  onChange={handleChange}
                  maxLength={10}
                  required
                  className="w-full focus:outline-none focus:border-navy"
                  style={inputStyle}
                />
              </div>

              <div className="flex flex-col col-span-2 gap-4">
                <label
                  htmlFor="contactEmail"
                  className="after:content-['*'] after:text-red-500 after:ml-1"
                  style={labelStyle}
                >
                  담당자 이메일
                </label>
                <input
                  type="email"
                  id="contactEmail"
                  name="contactEmail"
                  placeholder="담당자 이메일을 입력해주세요."
                  value={formData.contactEmail}
                  onChange={handleChange}
                  required
                  className="w-full focus:outline-none focus:border-navy"
                  style={inputStyle}
                />
                <p className="text-[1.3rem] text-gray-5">
                  해당 이메일은 테스트 계정 발급 및 본 계약 절차 안내에 사용되오니, 정확하게 입력해
                  주세요.
                </p>
              </div>

              <div className="flex flex-col col-span-2 gap-4">
                <label
                  className="after:content-['*'] after:text-red-500 after:ml-1"
                  style={labelStyle}
                >
                  사업장 주소
                </label>
                <div className="flex gap-4 mb-2">
                  <input
                    type="text"
                    name="zipCode"
                    placeholder="우편번호"
                    value={formData.zipCode}
                    readOnly
                    className="w-[120px] focus:outline-none focus:border-navy"
                    style={inputStyle}
                  />
                  <button
                    type="button"
                    onClick={() => setIsPostcodeOpen(true)}
                    className="px-6 py-2 bg-[#032C6D] text-white text-[1.4rem] rounded-sm hover:bg-[#1e1d4a] transition-colors"
                  >
                    우편번호 찾기
                  </button>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <input
                    type="text"
                    name="address"
                    placeholder="도로명 주소를 입력해주세요."
                    value={formData.address}
                    readOnly
                    className="flex-1 w-full focus:outline-none focus:border-navy"
                    style={inputStyle}
                  />
                  <input
                    type="text"
                    name="addressDetail"
                    placeholder="상세 주소를 입력해주세요."
                    value={formData.addressDetail}
                    onChange={handleChange}
                    className="flex-1 w-full focus:outline-none focus:border-navy"
                    style={inputStyle}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* 서비스 정보 */}
          <section className="mb-[60px]">
            <div className="flex items-center gap-5 mb-12">
              <h2 className="text-[2rem] font-bold text-navy">서비스 정보</h2>
              <div className="flex-1 h-[1px] bg-gray-400" />
            </div>

            <div className="lg:px-12">
              <div className="mb-8 ">
                <label
                  className="after:content-['*'] after:text-red-500 after:ml-1 block mb-3"
                  style={labelStyle}
                >
                  1. 서비스 종류
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {Object.values(SERVICE_CONFIGS)
                    .filter(svc => svc.id !== "aicc")
                    .map(svc => (
                      <label
                        key={svc.id}
                        className={`flex items-center gap-4 p-4 rounded-sm border cursor-pointer transition-all ${
                          serviceType === svc.id
                            ? "border-[#4361ee] bg-[#f0f4ff]"
                            : "border-gray-1 bg-[#fafafa] hover:border-gray-3"
                        }`}
                      >
                        <input
                          type="checkbox"
                          name="serviceType"
                          value={svc.id}
                          checked={serviceType === svc.id}
                          onChange={() => handleServiceTypeChange(svc.id)}
                          className="w-4 h-4 accent-[#4361ee]"
                        />

                        <div>
                          <span className="text-[1.5rem] font-medium">{svc.name}</span>
                        </div>
                      </label>
                    ))}
                </div>
              </div>
              {config.purposeOptions && (
                <div className="mb-8">
                  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-3 gap-2">
                    <label
                      className="after:content-['*'] after:text-red-500 after:ml-1 block "
                      style={labelStyle}
                    >
                      2. 메시지 발송 목적
                    </label>
                    <div className="flex gap-1">
                      <span className="text-[1.3rem] text-gray-6">
                        해당하는 업체 유형을 선택해 주세요
                      </span>
                      <span className="text-[1.3rem] text-red-500"> (중복 선택 가능)</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {config.purposeOptions.map(opt => (
                      <label
                        key={opt.value}
                        className={`flex items-center gap-4 p-4 rounded-sm border cursor-pointer transition-all ${
                          purposes.includes(opt.value)
                            ? "border-[#4361ee] bg-[#f0f4ff]"
                            : "border-gray-1 bg-[#fafafa] hover:border-gray-3"
                        }`}
                      >
                        <input
                          type="checkbox"
                          name="purpose"
                          value={opt.value}
                          checked={purposes.includes(opt.value)}
                          onChange={e => {
                            const checked = e.target.checked;
                            setPurposes(prev =>
                              checked ? [...prev, opt.value] : prev.filter(v => v !== opt.value)
                            );
                          }}
                          className="w-4 h-4 accent-[#4361ee]"
                        />
                        <div>
                          <span className="text-[1.5rem] font-medium">{opt.label}</span>
                          {opt.hint && (
                            <span className="text-[1.3rem] text-gray-5 ml-2">({opt.hint})</span>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                  {purposes.includes("other") && (
                    <input
                      type="text"
                      name="purposeOther"
                      placeholder="내용을 입력해주세요."
                      value={formData.purposeOther}
                      onChange={handleChange}
                      className="w-full mt-3 focus:outline-none focus:border-navy"
                      style={inputStyle}
                    />
                  )}
                </div>
              )}
              {/* 메시징 서비스일 때 회사유형 중복 선택 */}
              {serviceType === "messaging" && config.companyTypeOptions && (
                <div className="mb-8">
                  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-3 gap-2">
                    <label
                      className="after:content-['*'] after:text-red-500 after:ml-1 block"
                      style={labelStyle}
                    >
                      3. 회사 유형
                    </label>
                    <div className="flex gap-1">
                      <span className="text-[1.3rem] text-gray-6">
                        해당하는 발송 목적을 선택해 주세요
                      </span>
                      <span className="text-[1.3rem] text-red-500"> (중복 선택 가능)</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {config.companyTypeOptions.map(opt => (
                      <label
                        key={opt.value}
                        className={`flex items-center gap-4 p-4 rounded-sm border cursor-pointer transition-all ${
                          companyTypes.includes(opt.value)
                            ? "border-[#4361ee] bg-[#f0f4ff]"
                            : "border-gray-1 bg-[#fafafa] hover:border-gray-3"
                        }`}
                      >
                        <input
                          type="checkbox"
                          name="companyType"
                          value={opt.value}
                          checked={companyTypes.includes(opt.value)}
                          onChange={e => {
                            const checked = e.target.checked;
                            setCompanyTypes(prev =>
                              checked ? [...prev, opt.value] : prev.filter(v => v !== opt.value)
                            );
                          }}
                          className="w-4 h-4 accent-[#4361ee]"
                        />
                        <div>
                          <span className="text-[1.5rem] font-medium">{opt.label}</span>
                          {opt.hint && (
                            <span className="text-[1.3rem] text-gray-5 ml-2">({opt.hint})</span>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                  {companyTypes.includes("other") && (
                    <input
                      type="text"
                      name="companyTypeOther"
                      placeholder="내용을 입력해주세요."
                      value={formData.companyTypeOther}
                      onChange={handleChange}
                      className="w-full mt-3 focus:outline-none focus:border-navy"
                      style={inputStyle}
                    />
                  )}
                </div>
              )}
              {serviceType === "aicc" && (
                <div className="mb-8">
                  <label
                    htmlFor="sdkDomain"
                    className="after:content-['*'] after:text-red-500 after:ml-1 block mb-3"
                    style={labelStyle}
                  >
                    2. 고객사 사이트 URL
                  </label>

                  <div className="flex flex-col gap-4">
                    <input
                      type="text"
                      id="sdkDomain"
                      name="sdkDomain"
                      placeholder="예) https://www.example.com"
                      value={formData.sdkDomain}
                      onChange={handleChange}
                      className="w-full focus:outline-none focus:border-navy"
                      style={inputStyle}
                      autoComplete="on"
                    />
                    <p className="text-[1.3rem] text-gray-5">
                      문자콜센터를 이용할 웹사이트 도메인 주소를 입력해주세요.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* 메시징 테스트 환경 정보 */}
          {serviceType === "messaging" && (
            <section className="mb-[60px]">
              <div className="flex items-center gap-5 mb-12">
                <h2 className="text-[2rem] font-bold text-navy">메시징 테스트 환경 정보</h2>
                <div className="flex-1 h-[1px] bg-gray-400" />
              </div>
              <div className="lg:px-12">
                {config.showAgentIp && (
                  <div className="mb-8">
                    <label
                      htmlFor="agentServerIp"
                      className="after:content-['*'] after:text-red-500 after:ml-1 block mb-3"
                      style={labelStyle}
                    >
                      1. Agent(테스트 프로그램) 설치 서버 IP
                    </label>
                    <p className="text-[1.4rem] text-gray-5 mb-3">
                      메시지 발송을 위해 Agent가 설치될 서버의 공인 IP를 입력해주세요. 방화벽 허용
                      및 보안 검증에 사용됩니다.
                    </p>
                    <input
                      type="text"
                      id="agentServerIp"
                      name="agentServerIp"
                      placeholder="예) 1.123.104.1"
                      value={formData.agentServerIp}
                      onChange={handleChange}
                      className="w-full focus:outline-none focus:border-navy"
                      style={inputStyle}
                    />
                    <ul className="mt-2 text-[1.3rem] ml-3 text-gray-5 list-disc pl-5 space-y-1">
                      <li>고정 IP만 허용됩니다.</li>
                      <li>NAT/프록시 사용 시 실제 외부 IP를 입력해주세요.</li>
                    </ul>
                  </div>
                )}

                {config.showWebhookUrl && (
                  <div className="mb-8">
                    <label
                      htmlFor="webhookUrl"
                      className="after:content-['*'] after:text-red-500 after:ml-1 block mb-3"
                      style={labelStyle}
                    >
                      2. 발송 결과를 받을 서버 주소 (Webhook URL) (IP:PORT)
                    </label>
                    <p className="text-[1.4rem] text-gray-5 mb-3">
                      고객님이 보낸 문자의 성공/실패 여부를 고객님의 시스템(서버)에서 바로 확인하기
                      위해 필요한 연결 주소입니다.
                    </p>
                    <input
                      type="text"
                      id="webhookUrl"
                      name="webhookUrl"
                      placeholder="예) http://1.123.104.1:9000"
                      value={formData.webhookUrl}
                      onChange={handleChange}
                      className="w-full focus:outline-none focus:border-navy"
                      style={inputStyle}
                      autoComplete="on"
                    />
                    <ul className="mt-2 text-[1.3rem] ml-3 text-gray-5 list-disc pl-5 space-y-1">
                      <li>HTTP/HTTPS 모두 지원합니다.</li>
                      <li>테스트 시 서버가 실행 중이어야 합니다.</li>
                      <li>방화벽에서 해당 PORT가 열려 있어야 합니다.</li>
                    </ul>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* 서류 제출 */}
          {config.businessTypes && config.businessTypes.length > 0 && (
            <section className="mb-[60px]">
              <div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      const link = document.createElement("a");
                      link.href = "/file/caller.hwp";
                      link.download = "발신번호 변작방지 및 사전동의서 양식.hwp";
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    className="px-4 py-2 border border-gray-3 rounded-sm text-[1.4rem] bg-gray-800 text-white hover:bg-gray-50 hover:text-gray-800 transition-colors"
                  >
                    발신번호 변작방지 및 사전동의서 다운로드
                  </button>
                </div>
                <div className="flex items-center gap-5 mb-12">
                  <h2 className="text-[2rem] font-bold text-navy">서류 제출</h2>
                  <div className="flex-1 h-[1px] bg-gray-400" />
                </div>
              </div>
              <div className="lg:px-12">
                <div className="flex mb-6 ">
                  {config.businessTypes.map((bt, index) => {
                    const isActive = businessType === bt.type;

                    return (
                      <button
                        key={bt.type}
                        type="button"
                        onClick={() => {
                          setBusinessType(bt.type);
                          setFiles({});
                          // 파일 input 요소들도 초기화
                          Object.values(fileInputRefs.current).forEach(input => {
                            if (input) input.value = "";
                          });
                        }}
                        className={`
                            px-6 py-3 text-[1.5rem] font-medium
                            transition-colors
                            min-w-60
                            ${
                              isActive
                                ? "bg-[#032C6D] text-white"
                                : "bg-white border border-gray-3 text-gray-6 hover:bg-gray-50"
                            }
                            ${
                              index === 0
                                ? "rounded-l-md"
                                : index === config.businessTypes!.length - 1
                                  ? "rounded-r-md"
                                  : "rounded-none"
                            }
                            -ml-px
                          `}
                      >
                        {bt.label}
                      </button>
                    );
                  })}
                </div>

                {currentBusinessConfig && (
                  <p className="text-[1.4rem] text-gray-5 mb-6">
                    {currentBusinessConfig.description}
                  </p>
                )}

                <div className="space-y-6">
                  {currentBusinessConfig?.files.map((field, idx) => (
                    <div key={field.id} className="flex items-start gap-4 flex-col sm:flex-row">
                      <label
                        className={`w-[240px] min-w-fit shrink-0 text-[1.5rem] ${
                          field.required ? "after:content-['*'] after:text-red-500 after:ml-1" : ""
                        }`}
                        style={{ fontWeight: 500 }}
                      >
                        {idx + 1}. {field.label}
                      </label>

                      <div className="flex-1 w-full">
                        <div className="flex items-center gap-4">
                          {/* 파일명 표시 input */}
                          <input
                            type="text"
                            placeholder={`${field.label}을 첨부해주세요.`}
                            value={files[field.id]?.name || ""}
                            readOnly
                            onClick={() => fileInputRefs.current[field.id]?.click()}
                            className="flex-1 cursor-pointer focus:outline-none"
                            style={{ ...inputStyle, backgroundColor: "#fff" }}
                          />

                          {/* 파일 선택 버튼 */}
                          <label className="px-4 py-4 border border-gray-3 rounded-sm text-[1.4rem] cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap">
                            파일 선택
                            <input
                              type="file"
                              ref={el => {
                                fileInputRefs.current[field.id] = el;
                              }}
                              onChange={e => handleFileChange(field.id, e)}
                              className="hidden"
                            />
                          </label>
                        </div>

                        {field.helpText && (
                          <p className="text-[1.3rem] text-[#4361ee] mt-2">{field.helpText}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* 버튼 */}
          <div className="flex justify-center gap-4">
            <button
              type="submit"
              disabled={submitMutation.isPending}
              className={`px-8 py-4 bg-[#032C6D] text-white rounded-sm text-[1.6rem] font-medium transition-colors ${
                submitMutation.isPending ? "opacity-50 cursor-not-allowed" : "hover:bg-[#1e1d4a]"
              }`}
            >
              {submitMutation.isPending ? "신청 처리 중..." : "서비스 이용 신청하기"}
            </button>
          </div>
        </form>

        {/* Daum Postcode Modal */}
        {isPostcodeOpen && (
          <div
            className="fixed inset-0 flex justify-center items-center z-[1001]"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
            onClick={() => setIsPostcodeOpen(false)}
          >
            <div
              className="overflow-hidden bg-white rounded-sm"
              style={{ width: "500px", height: "505px" }}
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-[1.8rem] font-bold">우편번호 검색</h3>
                <button
                  type="button"
                  onClick={() => setIsPostcodeOpen(false)}
                  className="text-[2.4rem] text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </div>
              <DaumPostcode onComplete={handlePostcodeComplete} style={{ height: "505px" }} />
            </div>
          </div>
        )}

        {/* Alert/Success Modal */}
        {showModal && (
          <div
            className="fixed inset-0 flex justify-center items-center z-[1000]"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
          >
            <div
              className="text-center bg-white"
              style={{
                padding: "30px 40px",
                borderRadius: "8px",
                width: modalType === "success" ? "480px" : "400px",
                boxShadow: "0 5px 15px rgba(0, 0, 0, 0.3)",
              }}
            >
              {modalType === "alert" ? (
                <>
                  <h3
                    className="text-left pb-3 mb-[15px]"
                    style={{
                      borderBottom: "1px solid #e5e5e5",
                      fontSize: "1.8rem",
                      fontWeight: "bold",
                      color: "#1a1a1a",
                    }}
                  >
                    알림
                  </h3>
                  <p
                    className="py-5 mb-[10px] leading-[1.5] whitespace-pre-wrap"
                    style={{
                      fontSize: "1.5rem",
                      color: "#1a1a1a",
                    }}
                  >
                    {modalMessage}
                  </p>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="cursor-pointer transition-colors hover:bg-black px-6 py-3 bg-[#032C6D] text-white rounded text-[1.5rem]"
                    >
                      확인
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h3
                    className="text-[2rem] font-bold mb-3 border-b text-left pb-3"
                    style={{ borderColor: "#e5e5e5" }}
                  >
                    {config.successModal.title}
                  </h3>
                  {/* {requestId && (
                    <div className="p-4 mb-3 border border-blue-200 rounded-lg bg-blue-50">
                      <p className="text-[1.5rem] text-gray-7 mb-2">
                        고객님의 요청번호는{" "}
                        <strong className="text-[#4361ee] text-[1.8rem]">{requestId}번</strong>
                        입니다.
                      </p>
                      <p className="text-[1.3rem] text-gray-5">
                        요청번호는 조회 시 사용되오니 메모하시길 바랍니다.
                      </p>
                    </div>
                  )} */}
                  <div className="text-[1.5rem] text-gray-7 leading-[1.8] mb-8">
                    {config.successModal.message}
                  </div>
                  <div className="flex justify-center gap-4">
                    {config.successModal.showTestButton && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowModal(false);
                          navigate("/service/test");
                        }}
                        className="px-6 py-3 bg-[#032C6D] text-white rounded text-[1.5rem] hover:bg-[#1e1d4a] transition-colors"
                      >
                        {config.successModal.testButtonLabel}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        navigate("/");
                      }}
                      className="px-6 py-3 rounded text-[1.5rem] hover:bg-gray-700  bg-gray-800 text-white transition-colors"
                    >
                      확인
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </Container>
  );
};

export default ServiceSubmit;
